import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Card, CardContent, Chip, Divider, FreeSoloDropdown, Stack, Typography} from 'views/components';
import {Translate, translate, CardSelector} from 'views/module';
import {Delete} from 'views/icons';

const LanguageSection = (props) => {
  const {
    onLanguagesChange,
    languages,
    setDisableNext,
  } = props;

  const DEFAULT_LANGUAGE_DETAIL = {
    name: null,
    fluency: null,
  };

  const [languagesSubsectionState, setLanguagesSubsectionState] = React.useState({chipOrForm: 'CHIP', isLanguageSelectedOther: false});
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [languageDetails, setLanguageDetails] = React.useState(DEFAULT_LANGUAGE_DETAIL);

  // Predefined list of caregiving skills
  const languageKeys = Object.keys(translate('caregiverScreening.SkillsAndLanguages.AvailableLanguages'));
  const fluencyOptions = Object.keys(translate('caregiverScreening.SkillsAndLanguages.Fluency'));
  const availableLanguagesForFreeSolo = languageKeys.filter((option) => !languages.some((lang) => lang.name === option));

  const OTHER = translate('caregiverScreening.SkillsAndLanguages.AvailableLanguages.Other');

  React.useEffect(() => {
    setIsFormValid(Boolean(languageDetails.name?.trim() && languageDetails.fluency));
  });

  React.useEffect(() => {
    setDisableNext(languagesSubsectionState.chipOrForm === 'FORM');
  }, [languagesSubsectionState.chipOrForm]);

  const handleLanguageClick = (languageKey) => {
    const languagePreData = languages.find((l) => l.name === languageKey);
    let detailsToSet;
    const isOtherSelected = (getLanguageLabel(languageKey) === OTHER);
    if (languagePreData) {
      detailsToSet = languagePreData;
    } else {
      detailsToSet = isOtherSelected ? {name: null, fluency: null} : {name: languageKey, fluency: null};
    }
    setLanguageDetails(detailsToSet);
    setLanguagesSubsectionState({chipOrForm: 'FORM', isLanguageSelectedOther: isOtherSelected});
  };

  const handleFluencyChange = (newValue) => {
    if (newValue && newValue !== languageDetails.fluency) { // If it is not the currently selected value
      const updatedLanguageDetails = {...languageDetails, fluency: newValue};
      setLanguageDetails(updatedLanguageDetails);

      if (languageDetails.name) {
        handleSaveLanguage(updatedLanguageDetails);
      }
    }
    if (!languagesSubsectionState.isLanguageSelectedOther) {
      cancelForm();
    }
  };

  const handleSaveLanguage = (updatedLanguageDetails) => {
    const oldLanguagesFiltered = languages.filter((language) => language.name !== updatedLanguageDetails.name);

    const newLanguages = [
      ...oldLanguagesFiltered,
      updatedLanguageDetails,
    ];

    onLanguagesChange(newLanguages); // Update parent component's languages state with new/modified language entry
  };

  const handleRemoveLanguage = (languageName) => {
    const updatedLanguages = languages.filter((l) => l.name !== languageName);
    onLanguagesChange(updatedLanguages);
  };

  const cancelForm = () => {
    setLanguagesSubsectionState({chipOrForm: 'CHIP', isLanguageSelectedOther: false});
    setLanguageDetails(DEFAULT_LANGUAGE_DETAIL);
  };

  const getLanguageLabel = (languageId) => translate(`caregiverScreening.SkillsAndLanguages.AvailableLanguages.${languageId}`);

  return (
    <Box>
      <Typography variant="h6" gutterBottom align="center" fontWeight={600}>
        <Translate expression='caregiverScreening.SkillsAndLanguages.LanguagesTitle'/>
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 3}}>
        <Translate expression='caregiverScreening.SkillsAndLanguages.LanguagesSubtitle'/>
      </Typography>

      {
        languagesSubsectionState.chipOrForm === 'CHIP' &&
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mt: 2,
          }}
        >
          {
            languageKeys.map((languageKey) => {
              const isLanguageSelected = languages.some((language) => language.name === languageKey);
              return (
                <Chip
                  key={languageKey}
                  label={getLanguageLabel(languageKey)}
                  onClick={
                    () => {
                      handleLanguageClick(languageKey);
                    }
                  }
                  color={isLanguageSelected ? 'primary' : 'default'}
                  variant={isLanguageSelected ? 'filled' : 'outlined'}
                  sx={{
                    'cursor': 'pointer',
                    'transition': 'all 0.2s ease-in-out',
                    'boxShadow': isLanguageSelected ? 2 : 0,
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: 2,
                    },
                  }}
                />
              );
            })
          }
        </Box>
      }
      {
        languagesSubsectionState.chipOrForm === 'FORM' &&
        <Card sx={{boxShadow: 2, borderRadius: 2, p: 0, mt: 2, mb: 2}}>
          <Box sx={{pt: 2, pl: 2, pr: 2}}>
            <Typography variant="h6" fontWeight="bold">
              {languagesSubsectionState.isLanguageSelectedOther ? OTHER : getLanguageLabel(languageDetails.name)}
            </Typography>
            <Divider sx={{mt: 1}} />
          </Box>

          <CardContent sx={{p: {xs: 1, sm: 2}}}>
            <Stack spacing={2}>
              {
                languagesSubsectionState.isLanguageSelectedOther &&
                <Box>
                  <Typography color="text.secondary" variant="subtitle2" sx={{mb: 1}}>
                    {translate('caregiverScreening.SkillsAndLanguages.LanguagesSelect')}
                  </Typography>

                  <FreeSoloDropdown
                    options={availableLanguagesForFreeSolo}
                    value={languageDetails.name}
                    onChange={(newValue) => {
                      setLanguageDetails({...languageDetails, name: newValue});
                    }}
                    placeholder={translate('caregiverScreening.SkillsAndLanguages.LanguagesSelectPlaceholder')}
                    fullWidth
                    required
                    size="small"
                  />
                </Box>
              }

              {
                <Typography color="text.secondary" variant="subtitle2" sx={{mb: 1}}>
                  {translate('caregiverScreening.SkillsAndLanguages.FluencyHelpText')}
                </Typography>
              }
              {/* Fluency option buttons */}
              <CardSelector
                selectedValue={languageDetails.fluency}
                onSelectionChange = {(fluency) => handleFluencyChange(fluency)}
                options={fluencyOptions.map((opt) => {
                  return {
                    id: opt,
                    label: translate(`caregiverScreening.SkillsAndLanguages.Fluency.${opt}`),
                  };
                })}
              />
              <Box sx={{p: 1}}>
                <Divider />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
                  gap: 2,
                }}
              >
                {/* Remove button */}
                <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => {
                      handleRemoveLanguage(languageDetails.name);
                      cancelForm();
                    }}
                    size="small"
                  >
                    <Translate expression='caregiverScreening.Certifications.RemoveButton'/>
                  </Button>
                </Box>

                {/* Save button */}
                {
                  languagesSubsectionState.isLanguageSelectedOther &&
                  <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => {
                        handleSaveLanguage(languageDetails);
                        cancelForm();
                      }}
                      disabled={!isFormValid}
                      size="small"
                    >
                      <Translate expression='caregiverScreening.Certifications.SaveButton'/>
                    </Button>
                  </Box>
                }
              </Box>
            </Stack>
          </CardContent>
        </Card>
      }

    </Box>
  );
};

LanguageSection.propTypes = {
  onLanguagesChange: PropTypes.func.isRequired,
  languages: PropTypes.arrayOf(PropTypes.object).isRequired,
  setDisableNext: PropTypes.func.isRequired,
};

export default LanguageSection;
